/* eslint-disable no-console */
import { isPipelineType, PipelineSeriesByRegionColour, PipelineSeriesOnBuild, PipelineType } from '@nova/types';

import { TexturePipelineBase } from './glPipelines/pipeline-texBase';
import { MaskedTexturePipeline } from './glPipelines/pipeline-texture-masking';
import { OverlayedTexturePipeline } from './glPipelines/pipeline-texture-overlay';
import { ScaledTexturePipeline } from './glPipelines/pipeline-texture-scale';
import { SummationTexturePipeline } from './glPipelines/pipeline-texture-summation';
import { TiledTexturePipeline } from './glPipelines/pipeline-texture-tiling';
import { TintedTexturePipeline } from './glPipelines/pipeline-texture-tinting';
import {
  PipelineSeriesEntry_UniformParams,
  pipelineSeriesParamConversion,
  PipelineSeriesToRender,
  TexturePipelineSet
} from './glPipelines/utils/pipeline-utils-types';
import { IScaleParams } from './models/scale-params.interface';
import { IScalePipelineResult } from './models/scale-pipeline-result.interface';
import { PipelineSetByColourRegion } from './texture-manip-drawSeriesByRegion';

/**
 * These operations may be fairly generic, and not lkely to be used solo.
 */
export class StaticPipelineOperations {
  public static GetDefault(imageData: ImageData, gl?: WebGLRenderingContext): Uint8Array {
    const pipeline = new TexturePipelineBase();
    pipeline.Init(imageData.width, imageData.height, gl);
    pipeline.CreateTextures([imageData]);
    const pixelData = pipeline.DrawOnceForPixelData();
    pipeline.Destroy();
    return pixelData;
  }

  public static GetTiled(imageData: ImageData, finalWidth: number, finalHeight: number, gl?: WebGLRenderingContext): Uint8Array {
    const pipeline = new TiledTexturePipeline(finalWidth / imageData.width, finalHeight / imageData.height);
    pipeline.Init(finalWidth, finalHeight, gl);
    pipeline.CreateTextures([imageData]);
    const pixelData = pipeline.DrawOnceForPixelData();
    pipeline.Destroy();
    return pixelData;
  }

  public static GetMasked(imageToMaskData: ImageData, imageAsMaskData: ImageData, invertMaskEffect: boolean, gl?: WebGLRenderingContext): Uint8Array {
    const pipeline = new MaskedTexturePipeline(invertMaskEffect);
    pipeline.Init(imageToMaskData.width, imageToMaskData.height, gl);
    pipeline.CreateTextures([imageToMaskData, imageAsMaskData]);
    const pixelData = pipeline.DrawOnceForPixelData();
    pipeline.Destroy();
    return pixelData;
  }

  public static GetOverlayed(imageBottomData: ImageData, imageTopData: ImageData, gl?: WebGLRenderingContext): Uint8Array {
    const pipeline = new OverlayedTexturePipeline();
    pipeline.Init(imageBottomData.width, imageBottomData.height, gl);
    pipeline.CreateTextures([imageBottomData, imageTopData]);
    const pixelData = pipeline.DrawOnceForPixelData();
    pipeline.Destroy();
    return pixelData;
  }

  public static GetColourTinted(imageData: ImageData, colour: number[], gl?: WebGLRenderingContext): Uint8Array {
    const pipeline = new TintedTexturePipeline();
    pipeline.Init(imageData.width, imageData.height, gl);
    pipeline.CreateTextures([imageData]);
    const pixelData = pipeline.DrawOnceForPixelData(colour);
    pipeline.Destroy();
    return pixelData;
  }

  public static GetScaled(imageData: ImageData, scale: IScaleParams, gl?: WebGLRenderingContext): IScalePipelineResult {
    const pipeline = new ScaledTexturePipeline(scale);

    pipeline.Init(imageData.width, imageData.height, gl);
    const newWidthPx = pipeline.canvasWidth;
    const newHeightPx = pipeline.canvasHeight;

    pipeline.CreateTextures([imageData]);
    const pixelData = pipeline.DrawOnceForPixelData();
    pipeline.Destroy();

    return { pixelData, newWidthPx, newHeightPx };
  }

  /**
   * Will run through and draw the given series of texture-based webgl piepline data. This makes a good few presumptions:
   * 1. The pipelines have already been instantiated with a gl context,
   * 2. given width/height props, and
   * 3. contains a default-texture pipeline to initiate the process.
   * This function just serves common operations, hence being private, and will need to be followed up with some sort of final draw call.
   * @param texturePipelines
   * @param seriesBuilt
   * @returns
   */
  private static RunPipelineSeries(texturePipelines: TexturePipelineSet, seriesBuilt: PipelineSeriesOnBuild): PipelineSeriesEntry_UniformParams {
    // console.log(`StaticPipelineOperations::RunPipelineSeries: seriesBuilt:`, seriesBuilt);
    if (seriesBuilt.length === 0) {
      throw new Error(`StaticPipelineOperations:RunPipelineSeries seriesBuilt length 0, should be impossible at this point.`);
    }

    // Perform any/all required data conversions to prepare for drawing.
    const pipelineSeriesToRender: PipelineSeriesToRender = [];

    for (const entry of seriesBuilt) {
      const ConversionFunc = pipelineSeriesParamConversion[entry.type];

      // Convert parameters to those usable here, if necessary (pre-defined)
      const params = ConversionFunc ? ConversionFunc(entry.params) : entry.params;

      if (!isPipelineType(entry.type)) {
        console.error(`StaticPipelineOperations::RunPipelineSeries: Invalid pipeline type <${entry.type}> in seriesBuilt. Cannot continue.`);
        continue;
      }

      pipelineSeriesToRender.push({
        type: entry.type,
        params
      });
    }
    // console.log(`pipelineSeriesToRender:`, JSON.stringify(pipelineSeriesToRender));

    // Capture the last set of data for it's own specific draw consideration
    const lastData = pipelineSeriesToRender.pop();

    // * Always start with the default texture, then carry on from there.
    // This is to be absolutely sure that a copy is being made at this point, and the original texture still stands as is,
    // in case there's another reference elsewhere.
    texturePipelines[PipelineType.textureDefault].UseProgram();
    let nextTexture: WebGLTexture = texturePipelines[PipelineType.textureDefault].DrawToTexture({});
    // console.log(`NEXT TEXTURE PIXEL DATA (default):`, texturePipelines[PipelineType.textureDefault].GetTexturePixelData(nextTexture));

    // ! nextTexture first comes in as blank! Hence it needs it's own cached texture... oi.
    // TODO: This is kinda hideous... find a way to abstract this off where the property is created and draw calls made?
    // Not possible since each pipeline doesn't have awareness of the next in line.
    let regionMaskNextTex: WebGLTexture | undefined;

    for (const entry of pipelineSeriesToRender) {
      const pipeline = texturePipelines[entry.type];
      regionMaskNextTex && pipeline.regionMask?.UpdateBaseTexture(regionMaskNextTex);
      pipeline.SetGlTextures([nextTexture]);
      pipeline.UseProgram();

      nextTexture = pipeline.DrawToTexture(entry.params);
      regionMaskNextTex = nextTexture;

      // pipeline.regionMask?.UpdateBaseTexture(nextTexture);

      // console.log(`NEXT TEXTURE PIXEL DATA (${entry.type}):`, texturePipelines[PipelineType.textureDefault].GetTexturePixelData(nextTexture));
    }

    const pipeline = texturePipelines[lastData!.type];
    regionMaskNextTex && pipeline.regionMask?.UpdateBaseTexture(regionMaskNextTex);
    pipeline.SetGlTextures([nextTexture]);
    pipeline.UseProgram();

    // console.log(`RunPipelineSeries: lastData:`, lastData);
    return lastData!;
  }

  public static DrawPipelineSeriesToCanvas(texturePipelines: TexturePipelineSet, seriesBuilt: PipelineSeriesOnBuild): PipelineType {
    // No series passed through, just draw and leave. Cannot pass anything back.
    if (seriesBuilt.length === 0) {
      // console.log(`No "last data", will only draw default texture to canvas.`);
      texturePipelines[PipelineType.textureDefault].UseProgram();
      texturePipelines[PipelineType.textureDefault].Draw({});
      return PipelineType.textureDefault;
    }

    const lastData: PipelineSeriesEntry_UniformParams = this.RunPipelineSeries(texturePipelines, seriesBuilt);

    texturePipelines[lastData.type].Draw(lastData.params);
    return lastData.type;
  }

  public static DrawPipelineSeriesToTexture(
    texturePipelines: TexturePipelineSet,
    seriesBuilt: PipelineSeriesOnBuild,
    scaleDownFinalRegionMaskResult: boolean
  ): WebGLTexture {
    if (seriesBuilt.length < 1) { return StaticPipelineOperations.createDefaultTexture(texturePipelines); }
    return StaticPipelineOperations.createSeriesTexture(texturePipelines, seriesBuilt, scaleDownFinalRegionMaskResult);
  }

  /**
   *  Assumes all the pipelines have their gl reference set and their buffers are initialized.
   */
  private static createSeriesTexture(texturePipelines: TexturePipelineSet, seriesBuilt: PipelineSeriesOnBuild, scaleDownFinalRegionMaskResult: boolean) {
    const lastData: PipelineSeriesEntry_UniformParams = this.RunPipelineSeries(texturePipelines, seriesBuilt);
    const lastPipeline = texturePipelines[lastData.type];
    if (lastPipeline.regionMask) {
      lastPipeline.regionMask.scaleDownResult = scaleDownFinalRegionMaskResult;
    }
    const texture: WebGLTexture = lastPipeline.DrawToTexture(lastData.params);
    return texture;
  }

  /**
     * @param texturePipelines
     */
  private static createDefaultTexture(texturePipelines: TexturePipelineSet): WebGLTexture {
    const pipelineInstance: TexturePipelineBase = texturePipelines[PipelineType.textureDefault];
    pipelineInstance.UseProgram();
    pipelineInstance.DrawToTexture({});
    return pipelineInstance;
  }

  public static RunPipelineSeriesByRegionColour(
    pipelinesByColourRegion: PipelineSetByColourRegion,
    sumPipeline: SummationTexturePipeline,
    seriesByRegionColour: PipelineSeriesByRegionColour,
    baseTexture: WebGLTexture): WebGLTexture | undefined {
    // console.log(`StaticPipelineOperations::RunPipelineSeriesByRegionColour: series`, seriesByRegionColour);

    let prevTextureOutput: WebGLTexture | undefined;

    for (const colourName in seriesByRegionColour) {
      if (!Object.prototype.hasOwnProperty.call(seriesByRegionColour, colourName)) { continue; }
      // console.log(`For colour <${colourName}>...`);

      const pipelineSet = pipelinesByColourRegion[colourName];
      if (!pipelineSet) {
        console.error(`A set of pipelines was not established for the colour <${colourName}>`);
        return undefined;
      }

      for (const type in pipelineSet) {
        if (!Object.prototype.hasOwnProperty.call(pipelineSet, type)) { continue; }
        const regionMaskComp = pipelineSet[type].regionMask;
        if (!regionMaskComp) { continue; }

        // Reset the base texture that begins the process, since it gets updated gradually
        // for each series type that imbues it's effects (base > tint > colour matrix > etc.)
        regionMaskComp.UpdateBaseTexture(baseTexture);
        // Reset each pipeline's "scaleDownResult" property, since we only want it to happen on the LAST regionMask-having pipeline in each colour run.
        regionMaskComp.scaleDownResult = false;
      }

      const seriesData: PipelineSeriesOnBuild = seriesByRegionColour[colourName];
      // console.log(`- seriesData:`, seriesData);

      let textureResult: WebGLTexture = this.DrawPipelineSeriesToTexture(pipelineSet, seriesData, true);

      // If we have a texture cached from the last run, blend the 2 together additively.
      if (prevTextureOutput) {
        sumPipeline.SetGlTextures([prevTextureOutput, textureResult]);
        sumPipeline.UseProgram();
        // Set it to this updated texture object, so the same object can be used to overlay the base, regardless of whether or not it came through here.

        // ! TEST Base draw just to visualize each step while frame-stepping/debugging. Otherwise, do not draw, just "DrawToTexture".
        // sumPipeline.Draw({});
        textureResult = sumPipeline.DrawToTexture({});
      }

      prevTextureOutput = textureResult;
    }

    return prevTextureOutput;
  }
}
